/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, Inject, Input, OnInit, signal,
} from '@angular/core';
import { ProjectNavigationService } from '../project-navigation.service';

// /projects/:projectId in the 2026 design: the description, with applying in the same card.
// Applying records the application once, and only then opens survey_link with $id$, $email$ and $name$ filled in;
// afterwards the card offers "Open the survey again", so a blocked pop-up isn't a dead end.
@Component({
  selector: 'ut-project-page',
  templateUrl: './project-page.component.html',
  styleUrls: ['./project-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProjectPageComponent implements OnInit {
  // From the `singleproject` state's resolves. `user` is null when logged out.
  @Input({ required: true }) project: any;
  @Input() user: any;

  applied = signal(false);
  popupBlocked = signal(false);
  applyFailed = signal(false);
  prev = signal<any>(null);
  next = signal<any>(null);
  fullUrl = '';
  loginUrl = '';
  private applying = false;

  constructor(
    @Inject('ProjectService') private ProjectService: any,
    @Inject('$state') private $state: any,
    @Inject('$location') private $location: any,
    private navigation: ProjectNavigationService,
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.applied.set(!!this.project.user_has_applied);
    this.fullUrl = this.$state.href('singleproject', { projectId: this.project.slug || this.project.id }, { absolute: true });
    this.loginUrl = `/login?return_to=${encodeURIComponent(this.$location.absUrl())}`;
    this.setupPrevNext();
  }

  // Archived listings are only shown to admins.
  get hidden(): boolean {
    return this.project.state === 'archived' && this.user?.user_type !== 'admin';
  }

  apply(): void {
    // A second click while the request is in flight would post and open the survey again.
    if (this.applying) return;
    this.applying = true;
    this.applyFailed.set(false);
    // Opened blank on the click, so the browser allows it, then sent to the survey once the application is saved.
    // If the application fails, the blank tab is closed and the survey never opens.
    const survey = this.project.survey_link ? window.open('', '_blank') : null;
    if (survey) survey.opener = null;
    this.ProjectService.apply(this.project.id).then(() => {
      this.project.user_has_applied = true;
      this.applied.set(true);
      if (survey) survey.location.href = this.surveyUrl();
      this.popupBlocked.set(!!this.project.survey_link && !survey);
      // The Apply button is replaced, so focus goes to what replaced it.
      setTimeout(() => this.el.nativeElement.querySelector<HTMLElement>('.project__apply-area button, .project__apply-area [tabindex="-1"]')?.focus());
    }, () => {
      survey?.close();
      this.applyFailed.set(true);
    }).finally(() => {
      this.applying = false;
    });
  }

  openSurvey(): void {
    if (!this.project.survey_link) return;
    // Without the `noopener` feature so a blocked pop-up can be told apart (window.open returns null).
    const survey = window.open(this.surveyUrl(), '_blank');
    if (survey) survey.opener = null;
    this.popupBlocked.set(!survey);
  }

  private surveyUrl(): string {
    return this.project.survey_link
      .replace('$id$', encodeURIComponent(this.user.platform_id))
      .replace('$email$', encodeURIComponent(this.user.email))
      .replace('$name$', encodeURIComponent(this.user.name));
  }

  private load(neighbour: any, target: { set(value: any): void }): void {
    if (!neighbour) return;
    target.set(neighbour);
    this.ProjectService.find(neighbour.id).then((res) => target.set(res.data), () => {});
  }

  // Neighbours in the board list the project was opened from, or else the ones the API returns.
  private setupPrevNext(): void {
    const { feed } = this.navigation;
    const index = feed ? feed.feedItems.findIndex((p) => p.id === this.project.id) : -1;
    if (index < 0) {
      // The API's neighbours may carry only an id; the full project has the slug and title the links need.
      this.load(this.project.previous, this.prev);
      this.load(this.project.next, this.next);
      return;
    }
    this.prev.set(feed.feedItems[index - 1] || null);
    if (feed.feedItems[index + 1]) {
      this.next.set(feed.feedItems[index + 1]);
    } else if (!feed.isLastPage) {
      feed.loadMore().then(() => this.next.set(feed.feedItems[index + 1] || null));
    }
  }
}
